// As per https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error
// The stack property is supported by all modern browsers even though it's not yet offical part of the JS spec.
// So we should use it to get error stack traces and filter errors based on it as well.

import type { Agent } from "@newrelic/browser-agent/loaders/agent";

const BLACKLISTED_ERROR_STRINGS: RegExp[] = [/optimizely/i, /gtm/i];

export function shouldIgnoreErrorEvent(
    errorEvent: ErrorEvent,
    enableStackTraceTestForMapsUrl = false,
) {
    // ignore error events with empty error messages (how can this happen?)
    if (errorEvent.message.trim().length === 0) {
        return true;
    }

    // if a blacklisted string is found either in the error events source js filename or message, ignore the error
    if (
        BLACKLISTED_ERROR_STRINGS.some(
            (regexp) =>
                regexp.test(errorEvent.filename) ||
                regexp.test(errorEvent.message),
        )
    )
        return true;

    return shouldIgnoreError(errorEvent.error, enableStackTraceTestForMapsUrl);
}

export function shouldIgnoreError(
    error: Parameters<Agent["noticeError"]>[0],
    enableStackTraceTestForMapsUrl = false,
): boolean {
    try {
        const errorMessage = typeof error === "string" ? error : error.message;

        // ignore errors with empty error message (how can this happen?)
        if (errorMessage.trim().length === 0) {
            return true;
        }

        // if a blacklisted string is found in the errors message, ignore the error
        if (
            BLACKLISTED_ERROR_STRINGS.some((regexp) =>
                regexp.test(errorMessage),
            )
        )
            return true;

        if (typeof error !== "string") {
            const errorStackTrace = error.stack ?? "";

            // if a blacklisted string is found in the errors stack trace, ignore the error
            if (
                BLACKLISTED_ERROR_STRINGS.some((regexp) =>
                    regexp.test(errorStackTrace),
                )
            )
                return true;

            // only log errors that are most likely maps errors
            // (the stacktrace usually contains filenames with full paths in which the error occurred and the
            // string "wcomv6/maps" is most likely only present in maps related files)
            // TODO: this needs to change later when we release maps in the apps or use maps web component in PHP
            if (
                enableStackTraceTestForMapsUrl &&
                !errorStackTrace.includes("wcomv6/maps")
            ) {
                return true;
            }
        }
    } catch {}

    return false;
}
